/* InternshipManagementScreen.js - Business Side
   10 Tabs: Interns, Programs, Applications, Create, Assignments, Reviews, Mentoring, Attendance, Reports, Settings
   FULLY INTEGRATED with StudentInternshipPortalScreen via shared AsyncStorage keys:
     - intern_portal_applications  (student → business submissions + business status updates)
     - intern_programs             (business → student published programs)
*/

import React, { useState, useEffect, useCallback } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity, ScrollView,
  RefreshControl, StatusBar, Alert, Modal, TextInput, ActivityIndicator,
  Image, Switch, Dimensions, Share, KeyboardAvoidingView, Platform, FlatList,
} from 'react-native';
import { MaterialCommunityIcons, Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';

const { width } = Dimensions.get('window');
const PRIMARY = '#B45309';

// ─── Shared keys (both screens read/write these) ──────────────────────────────
const SHARED = {
  PORTAL_APPS : 'intern_portal_applications',  // student submits here; business processes
  PROGRAMS    : 'intern_programs',             // business publishes here; student discovers
  DECISIONS   : 'intern_application_decisions', // business decisions → student notifications
};

// ─── API ─────────────────────────────────────────────────────────────────────
const API_BASE_URL = 'https://api.jobeng.ai';
const api = axios.create({
  baseURL: API_BASE_URL, timeout: 15000,
  headers: { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
});
api.interceptors.request.use(async c => {
  try {
    const t   = await AsyncStorage.getItem('auth_token');
    const bid = await AsyncStorage.getItem('company_id');
    if (t)   c.headers.Authorization = 'Bearer ' + t;
    if (bid) c.params = { ...c.params, company_id: parseInt(bid) };
  } catch {}
  return c;
}, e => Promise.reject(e));

const EP = {
  employees    : API_BASE_URL + '/api/data/employees',
  applications : API_BASE_URL + '/api/data/applications',
  jobs         : API_BASE_URL + '/api/data/jobs',
  interviews   : API_BASE_URL + '/api/data/interviews',
  companyProfile: API_BASE_URL + '/api/data/company_profiles',
};
const safeData = r =>
  (r?.data?.success && Array.isArray(r.data.data)) ? r.data.data : [];

const DEPARTMENTS   = ['Engineering','Marketing','Finance','HR','Design','Operations','Sales','Legal','Data Science','Product'];
const INTERN_STATUS = ['Active','Completed','Terminated','Extended','Converted to FT'];
const PROGRAM_TYPES = ['Summer Internship','Winter Internship','Year-Round','Co-Op','Graduate Program','Placement Year','Work Experience','Apprenticeship'];
const APP_STATUSES  = ['Submitted','Under Review','Interview Scheduled','Accepted','Rejected','More Info Requested','Shortlisted','Withdrawn'];

const fmtDate  = d => d ? new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '-';
const scColor  = v => v>=4?'#10B981':v>=3?'#F59E0B':'#EF4444';
const appColor = s => ({Submitted:'#8B5CF6','Under Review':'#F59E0B','Interview Scheduled':'#3B82F6',
  Accepted:'#10B981',Rejected:'#EF4444','More Info Requested':'#0891B2',Shortlisted:'#059669',Withdrawn:'#9CA3AF'})[s]||'#6B7280';
const mkId     = () => 'id_' + Date.now() + '_' + Math.random().toString(36).slice(2,6);

// ─────────────────────────────────────────────────────────────────────────────
export default function InternshipManagementScreen({ navigation, route }) {
  const jobId    = route?.params?.jobId    || null;
  const jobTitle = route?.params?.jobTitle || '';

  const [companyLogo, setCompanyLogo] = useState(route?.params?.companyLogo || null);
  const [companyName, setCompanyName] = useState(route?.params?.companyName || '');
  const [companyId,   setCompanyId]   = useState(null);

  const [activeTab,   setActiveTab]   = useState('interns');
  const [isLoading,   setIsLoading]   = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [appFilter,   setAppFilter]   = useState('All');

  // Data
  const [interns,     setInterns]     = useState([]);
  const [programs,    setPrograms]    = useState([]);
  const [internApps,  setInternApps]  = useState([]);   // from API
  const [portalApps,  setPortalApps]  = useState([]);   // from student portal
  const [reviews,     setReviews]     = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [attendance,  setAttendance]  = useState([]);
  const [stats,       setStats]       = useState({ total:0,active:0,completed:0,applications:0,programs:0,avgRating:0,newPortalApps:0 });

  // Modals
  const [showModal,       setShowModal]       = useState(false);
  const [selectedIntern,  setSelectedIntern]  = useState(null);
  const [showAppModal,    setShowAppModal]     = useState(false);
  const [selectedApp,     setSelectedApp]      = useState(null);
  const [showInterview,   setShowInterview]    = useState(false);
  const [showRejectModal, setShowRejectModal]  = useState(false);
  const [rejectReason,    setRejectReason]     = useState('');
  const [reviewerNotes,   setReviewerNotes]    = useState('');

  // Interview form
  const [interviewForm, setInterviewForm] = useState({
    date:'', time:'', type:'Video Call', link:'', location:'', notes:'',
    interviewerName:'', duration:'45',
  });

  // Crud forms
  const [internForm,  setInternForm]  = useState({ name:'',email:'',phone:'',university:'',course:'',department:'Engineering',mentor:'',start_date:'',end_date:'',stipend:'',program_type:'Summer Internship',status:'Active' });
  const [programForm, setProgramForm] = useState({ title:'',description:'',department:'Engineering',duration:'12',max_interns:'5',stipend_range:'',requirements:'',start_date:'',type:'Summer Internship',benefits:'' });
  const [assignForm,  setAssignForm]  = useState({ intern_name:'',project:'',task:'',deadline:'',priority:'medium',mentor:'' });
  const [reviewForm,  setReviewForm]  = useState({ intern_name:'',period:'',performance:0,communication:0,technical:0,initiative:0,teamwork:0,feedback:'',recommendation:'' });
  const [attForm,     setAttForm]     = useState({ intern_name:'',date:'',type:'Present',notes:'' });

  // ── Init ──────────────────────────────────────────────────────────────────
  useFocusEffect(useCallback(() => {
    loadCompanyLogo();
    loadPortalApps(); // refresh portal apps on every focus
  }, []));

  useEffect(() => { loadAll(); }, []);

  const loadCompanyLogo = async () => {
    try {
      const c = await AsyncStorage.getItem('company_profile');
      if (c) { const p = JSON.parse(c); setCompanyLogo(p.logo||null); if(p.name||p.company_name) setCompanyName(p.name||p.company_name); }
      const cid = await AsyncStorage.getItem('company_id');
      if (cid) {
        setCompanyId(cid);
        const r = await api.get(EP.companyProfile,{params:{company_id:cid}});
        if(r.data?.success&&r.data.data?.length>0){const lp=r.data.data[0];setCompanyLogo(lp.logo||null);if(lp.name||lp.company_name)setCompanyName(lp.name||lp.company_name);}
      }
    } catch {}
  };

  const loadPortalApps = async () => {
    try {
      const raw = await AsyncStorage.getItem(SHARED.PORTAL_APPS);
      if (raw) {
        const list = JSON.parse(raw);
        setPortalApps(Array.isArray(list) ? list : []);
      }
    } catch {}
  };

  const loadAll = async () => {
    setIsLoading(true);
    try {
      const cid = companyId || await AsyncStorage.getItem('company_id') || '1';
      const [r0, r1] = await Promise.allSettled([
        api.get(EP.employees,   {params:{company_id:parseInt(cid),limit:500}}).catch(()=>null),
        api.get(EP.applications,{params:{company_id:parseInt(cid),limit:500}}).catch(()=>null),
      ]);
      const emps = safeData(r0.status==='fulfilled'?r0.value:null).filter(e=>(e.employment_type||e.type||'').toLowerCase().includes('intern'));
      const apps = safeData(r1.status==='fulfilled'?r1.value:null).filter(a=>(a.job_type||a.position||'').toLowerCase().includes('intern'));
      if(emps.length>0) setInterns(emps);
      if(apps.length>0) setInternApps(apps);

      // Load local stores
      const [p,rv,asn,at,pa] = await Promise.all([
        AsyncStorage.getItem(SHARED.PROGRAMS),
        AsyncStorage.getItem('intern_reviews'),
        AsyncStorage.getItem('intern_assignments'),
        AsyncStorage.getItem('intern_attendance'),
        AsyncStorage.getItem(SHARED.PORTAL_APPS),
      ]);
      if(p)  setPrograms(JSON.parse(p));
      if(rv) setReviews(JSON.parse(rv));
      if(asn)setAssignments(JSON.parse(asn));
      if(at) setAttendance(JSON.parse(at));
      if(pa) { const list=JSON.parse(pa); setPortalApps(Array.isArray(list)?list:[]); }

      calcStats(emps.length>0?emps:interns, JSON.parse(pa||'[]'));
    } catch {} finally { setIsLoading(false); }
  };

  const onRefresh = async () => { setRefreshing(true); await loadAll(); setRefreshing(false); };

  const calcStats = (data, pApps=[]) => {
    setStats({
      total:      data.length,
      active:     data.filter(i=>(i.status||'').toLowerCase()==='active').length,
      completed:  data.filter(i=>(i.status||'').toLowerCase()==='completed').length,
      applications: internApps.length + (Array.isArray(pApps)?pApps.length:0),
      programs:   programs.length,
      avgRating:  reviews.length>0?(reviews.reduce((a,b)=>a+(b.performance||0),0)/reviews.length).toFixed(1):0,
      newPortalApps: Array.isArray(pApps)?pApps.filter(a=>a.status==='Submitted').length:0,
    });
  };

  // ── CRUD – Interns ────────────────────────────────────────────────────────
  const createIntern = async () => {
    if(!internForm.name||!internForm.email){Alert.alert('Error','Name and email required');return;}
    const payload = {...internForm,employment_type:'intern',company_id:parseInt(companyId)||0,created_at:new Date().toISOString(),id:mkId()};
    try{const r=await api.post(EP.employees,payload);if(r.data?.data?.id)payload.id=r.data.data.id;}catch{}
    setInterns(prev=>[payload,...prev]);
    setInternForm({name:'',email:'',phone:'',university:'',course:'',department:'Engineering',mentor:'',start_date:'',end_date:'',stipend:'',program_type:'Summer Internship',status:'Active'});
    setActiveTab('interns'); Alert.alert('Created','Intern added successfully');
  };

  const deleteIntern = intern => {
    Alert.alert('Remove','Remove '+(intern.name||'intern')+'?',[
      {text:'Cancel'},
      {text:'Remove',style:'destructive',onPress:async()=>{
        try{await api.delete(EP.employees+'/'+intern.id);}catch{}
        setInterns(prev=>prev.filter(i=>i.id!==intern.id));
        setShowModal(false);
      }},
    ]);
  };

  const convertToFullTime = intern => {
    Alert.alert('Convert','Convert '+(intern.name||'intern')+' to full-time?',[
      {text:'Cancel'},
      {text:'Convert',onPress:async()=>{
        try{await api.put(EP.employees+'/'+intern.id,{employment_type:'full-time',status:'active'});}catch{}
        setInterns(prev=>prev.map(i=>i.id===intern.id?{...i,status:'Converted to FT',employment_type:'full-time'}:i));
        Alert.alert('Converted',(intern.name||'Intern')+' is now a full-time employee');
      }},
    ]);
  };

  // ── CRUD – Programs ───────────────────────────────────────────────────────
  const createProgram = async () => {
    if(!programForm.title){Alert.alert('Error','Title required');return;}
    const payload = {
      ...programForm, id:mkId(), created_at:new Date().toISOString(),
      status:'Active', published:true, companyName, companyId,
      rating:4.8, applicants:0, reviews:0, matchScore:80,
      skills: (programForm.requirements||'').split(',').map(s=>s.trim()).filter(Boolean).slice(0,5),
      benefits: (programForm.benefits||'').split('\n').filter(Boolean),
      responsibilities: [],
    };
    const updated = [payload,...programs];
    setPrograms(updated);
    // ⭐ Publish to shared key so student portal can discover it
    await AsyncStorage.setItem(SHARED.PROGRAMS, JSON.stringify(updated));
    setProgramForm({title:'',description:'',department:'Engineering',duration:'12',max_interns:'5',stipend_range:'',requirements:'',start_date:'',type:'Summer Internship',benefits:''});
    Alert.alert('Published','Program created and published to Student Portal ✓');
  };

  const toggleProgramPublish = async (prog) => {
    const updated = programs.map(p=>p.id===prog.id?{...p,published:!p.published}:p);
    setPrograms(updated);
    await AsyncStorage.setItem(SHARED.PROGRAMS, JSON.stringify(updated));
  };

  const deleteProgram = async (id) => {
    Alert.alert('Delete','Delete this program?',[
      {text:'Cancel'},{text:'Delete',style:'destructive',onPress:async()=>{
        const updated=programs.filter(p=>p.id!==id);
        setPrograms(updated);
        await AsyncStorage.setItem(SHARED.PROGRAMS,JSON.stringify(updated));
      }},
    ]);
  };

  // ── CRUD – Assignments / Reviews / Attendance ─────────────────────────────
  const createAssignment = async () => {
    if(!assignForm.intern_name||!assignForm.project){Alert.alert('Error','Intern and project required');return;}
    const payload={...assignForm,id:mkId(),created_at:new Date().toISOString(),status:'In Progress'};
    const updated=[payload,...assignments]; setAssignments(updated);
    await AsyncStorage.setItem('intern_assignments',JSON.stringify(updated));
    setAssignForm({intern_name:'',project:'',task:'',deadline:'',priority:'medium',mentor:''});
    Alert.alert('Assigned','Task assigned');
  };

  const submitReview = async () => {
    if(!reviewForm.intern_name){Alert.alert('Error','Intern name required');return;}
    const avg=[reviewForm.performance,reviewForm.communication,reviewForm.technical,reviewForm.initiative,reviewForm.teamwork].filter(v=>v>0);
    const payload={...reviewForm,overall:avg.length>0?(avg.reduce((a,b)=>a+b,0)/avg.length).toFixed(1):0,id:mkId(),created_at:new Date().toISOString()};
    const updated=[payload,...reviews]; setReviews(updated);
    await AsyncStorage.setItem('intern_reviews',JSON.stringify(updated));
    setReviewForm({intern_name:'',period:'',performance:0,communication:0,technical:0,initiative:0,teamwork:0,feedback:'',recommendation:''});
    Alert.alert('Saved','Review submitted');
  };

  const recordAttendance = async () => {
    if(!attForm.intern_name||!attForm.date){Alert.alert('Error','Intern and date required');return;}
    const payload={...attForm,id:mkId()};
    const updated=[payload,...attendance]; setAttendance(updated);
    await AsyncStorage.setItem('intern_attendance',JSON.stringify(updated));
    setAttForm({intern_name:'',date:'',type:'Present',notes:''});
    Alert.alert('Recorded','Attendance saved');
  };

  // ── APPLICATION ACTIONS (the integration core) ────────────────────────────
  const updateAppStatus = async (app, newStatus, extra={}) => {
    const updatedApp = {
      ...app, status:newStatus, ...extra,
      updatedAt: new Date().toISOString(),
      reviewedBy: companyName || 'Recruiter',
    };

    // 1. Update portal apps in AsyncStorage (student reads this)
    const raw  = await AsyncStorage.getItem(SHARED.PORTAL_APPS);
    const list = raw ? JSON.parse(raw) : [];
    const updated = list.map(a => a.id===app.id ? updatedApp : a);
    await AsyncStorage.setItem(SHARED.PORTAL_APPS, JSON.stringify(updated));
    setPortalApps(updated);

    // 2. Try to update via API
    try {
      await api.put(EP.applications + '/' + app.id, { status: newStatus, ...extra });
    } catch {}

    return updatedApp;
  };

  const handleAccept = async (app) => {
    Alert.alert('Accept Application', `Accept ${app.studentName || app.candidateName || 'this applicant'} for ${app.programTitle}?`, [
      { text:'Cancel', style:'cancel' },
      { text:'Accept', onPress: async () => {
        await updateAppStatus(app, 'Accepted', {
          acceptedAt: new Date().toISOString(),
          offerDetails: `Congratulations! You have been accepted for ${app.programTitle} at ${companyName||'our company'}.`,
          reviewerNotes,
        });
        setShowAppModal(false);
        Alert.alert('✅ Accepted', 'The applicant has been notified via Student Portal.');
      }},
    ]);
  };

  const handleReject = async (app) => {
    if (!rejectReason.trim()) { Alert.alert('Required','Please enter a reason for rejection.'); return; }
    await updateAppStatus(app, 'Rejected', {
      rejectionReason: rejectReason,
      reviewerNotes,
    });
    setShowRejectModal(false);
    setShowAppModal(false);
    setRejectReason('');
    Alert.alert('Done','Application rejected. Student has been notified.');
  };

  const handleScheduleInterview = async () => {
    const app = selectedApp;
    if (!interviewForm.date || !interviewForm.time) { Alert.alert('Required','Date and time are required.'); return; }
    await updateAppStatus(app, 'Interview Scheduled', {
      interviewDate: interviewForm.date,
      interviewTime: interviewForm.time,
      interviewType: interviewForm.type,
      interviewLink: interviewForm.link,
      interviewLocation: interviewForm.location,
      interviewNotes: interviewForm.notes,
      interviewerName: interviewForm.interviewerName,
      interviewDuration: interviewForm.duration,
    });
    setShowInterview(false);
    setShowAppModal(false);
    setInterviewForm({date:'',time:'',type:'Video Call',link:'',location:'',notes:'',interviewerName:'',duration:'45'});
    Alert.alert('📅 Scheduled', `Interview for ${app.programTitle} scheduled for ${interviewForm.date} at ${interviewForm.time}. Student notified.`);
  };

  const handleShortlist = async (app) => {
    await updateAppStatus(app, 'Shortlisted', { reviewerNotes });
    setShowAppModal(false);
    Alert.alert('Shortlisted','Applicant moved to shortlist.');
  };

  const handleRequestInfo = async (app) => {
    await updateAppStatus(app, 'More Info Requested', {
      infoRequest: reviewerNotes || 'Please provide additional information about your application.',
    });
    setShowAppModal(false);
    Alert.alert('Sent','Request for more information sent to student.');
  };

  const handleUnderReview = async (app) => {
    await updateAppStatus(app, 'Under Review');
    setShowAppModal(false);
  };

  // ── Navigation helpers ────────────────────────────────────────────────────
  const handleEvaluate  = i => navigation.navigate('EvaluationAssessmentScreen',{companyLogo,companyName,employeeId:i.id,employeeName:i.name,employeePosition:i.position||i.course||'Intern',employeeEmail:i.email});
  const handleMakeOffer = i => navigation.navigate('JobOfferManagementScreen',{companyLogo,companyName,jobId,jobTitle,applicantName:i.name,applicantEmail:i.email,position:i.position||'Full-Time'});

  // ── PDF ───────────────────────────────────────────────────────────────────
  const generatePDF = async () => {
    const allApps = [...internApps, ...portalApps];
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><style>
      body{font-family:Arial;font-size:13px;color:#1F2937;padding:20px;}
      h1{color:#B45309;text-align:center;}h2{color:#B45309;border-bottom:1px solid #E5E7EB;padding-bottom:4px;margin-top:20px;}
      table{width:100%;border-collapse:collapse;margin:8px 0;}
      .d td,.d th{padding:6px 10px;border:1px solid #E5E7EB;font-size:11px;text-align:left;}
      .d th{background:#FFFBEB;color:#B45309;font-weight:700;}
      .chip{display:inline-block;padding:2px 8px;border-radius:8px;font-size:10px;font-weight:600;}
    </style></head><body>
    ${companyLogo?'<img src="'+companyLogo+'" style="width:50px;height:50px;border-radius:8px;float:left;margin-right:12px;" />':''}
    <div style="font-size:20px;font-weight:800;">${companyName||'Company'}</div>
    <div style="color:#6B7280;font-size:11px;">Internship Program Report — ${fmtDate(new Date().toISOString())}</div>
    <div style="clear:both;"></div><hr style="border-color:#B45309;">
    <h1>INTERNSHIP MANAGEMENT REPORT</h1>
    <table style="margin:16px 0;">
      <tr>
        <td style="text-align:center;padding:12px;background:#FFFBEB;border-radius:8px;width:16%;"><div style="font-size:24px;font-weight:800;color:#B45309;">${interns.length}</div><div style="font-size:10px;color:#6B7280;">Interns</div></td><td style="width:2%;"></td>
        <td style="text-align:center;padding:12px;background:#ECFDF5;border-radius:8px;width:16%;"><div style="font-size:24px;font-weight:800;color:#10B981;">${stats.active}</div><div style="font-size:10px;color:#6B7280;">Active</div></td><td style="width:2%;"></td>
        <td style="text-align:center;padding:12px;background:#EFF6FF;border-radius:8px;width:16%;"><div style="font-size:24px;font-weight:800;color:#3B82F6;">${programs.length}</div><div style="font-size:10px;color:#6B7280;">Programs</div></td><td style="width:2%;"></td>
        <td style="text-align:center;padding:12px;background:#F5F3FF;border-radius:8px;width:16%;"><div style="font-size:24px;font-weight:800;color:#8B5CF6;">${allApps.length}</div><div style="font-size:10px;color:#6B7280;">Applications</div></td><td style="width:2%;"></td>
        <td style="text-align:center;padding:12px;background:#FDF4FF;border-radius:8px;width:16%;"><div style="font-size:24px;font-weight:800;color:#EC4899;">${reviews.length}</div><div style="font-size:10px;color:#6B7280;">Reviews</div></td>
      </tr>
    </table>
    <h2>Active Interns</h2><table class="d"><tr><th>Name</th><th>University</th><th>Department</th><th>Mentor</th><th>Status</th></tr>
    ${interns.slice(0,20).map(i=>'<tr><td>'+(i.name||'')+'</td><td>'+(i.university||'')+'</td><td>'+(i.department||'')+'</td><td>'+(i.mentor||'')+'</td><td>'+(i.status||'')+'</td></tr>').join('')}</table>
    ${programs.length>0?'<h2>Published Programs</h2><table class="d"><tr><th>Title</th><th>Type</th><th>Department</th><th>Duration</th><th>Stipend</th><th>Published</th></tr>'+programs.map(p=>'<tr><td>'+(p.title||'')+'</td><td>'+(p.type||'')+'</td><td>'+(p.department||'')+'</td><td>'+(p.duration||'')+' wks</td><td>'+(p.stipend_range||p.stipend||'-')+'</td><td>'+(p.published?'Yes':'No')+'</td></tr>').join('')+'</table>':''}
    ${allApps.length>0?'<h2>Applications</h2><table class="d"><tr><th>Applicant</th><th>Program</th><th>Status</th><th>Applied</th></tr>'+allApps.slice(0,20).map(a=>'<tr><td>'+(a.studentName||a.candidateName||a.candidate_name||'')+'</td><td>'+(a.programTitle||a.position||'')+'</td><td>'+(a.status||'pending')+'</td><td>'+fmtDate(a.submittedAt||a.created_at)+'</td></tr>').join('')+'</table>':''}
    ${reviews.length>0?'<h2>Reviews</h2><table class="d"><tr><th>Intern</th><th>Overall</th><th>Recommendation</th></tr>'+reviews.slice(0,10).map(r=>'<tr><td>'+(r.intern_name||'')+'</td><td>'+(r.overall||0)+'/5</td><td>'+(r.recommendation||'')+'</td></tr>').join('')+'</table>':''}
    <div style="border-top:1px solid #E5E7EB;margin-top:30px;padding-top:8px;text-align:center;font-size:10px;color:#9CA3AF;">Confidential — ${companyName||'Company'} — ${fmtDate(new Date().toISOString())}</div>
    </body></html>`;
    try {
      const { uri } = await Print.printToFileAsync({ html, width:612, height:792 });
      Alert.alert('PDF Ready','',[ { text:'Share', onPress:async()=>{ if(await Sharing.isAvailableAsync()) await Sharing.shareAsync(uri,{mimeType:'application/pdf'}); } }, { text:'Print', onPress:async()=>{ try{await Print.printAsync({html});}catch{} } }, {text:'OK'} ]);
    } catch(e) { Alert.alert('Error',e.message); }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────
  const ScoreSlider = ({ label, value, onChange }) => (
    <View style={{ marginBottom:12 }}>
      <View style={{ flexDirection:'row', justifyContent:'space-between', marginBottom:4 }}>
        <Text style={{ fontSize:13, fontWeight:'600', color:'#374151' }}>{label}</Text>
        <Text style={{ fontSize:13, fontWeight:'700', color:scColor(value) }}>{value}/5</Text>
      </View>
      <View style={{ flexDirection:'row', gap:4 }}>
        {[1,2,3,4,5].map(s=>(
          <TouchableOpacity key={s} style={{ flex:1, height:30, borderRadius:6, backgroundColor:value>=s?scColor(s):'#F3F4F6', justifyContent:'center', alignItems:'center' }} onPress={()=>onChange(s)}>
            <Text style={{ color:value>=s?'#FFF':'#9CA3AF', fontWeight:'600', fontSize:12 }}>{s}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // ── TABS ─────────────────────────────────────────────────────────────────
  const TABS = [
    {k:'interns',      t:'Interns',    i:'account-group'},
    {k:'programs',     t:'Programs',   i:'school'},
    {k:'applications', t:'Apply',      i:'file-account'},
    {k:'create',       t:'Add',        i:'plus-circle'},
    {k:'assignments',  t:'Tasks',      i:'clipboard-list'},
    {k:'reviews',      t:'Reviews',    i:'star'},
    {k:'mentoring',    t:'Mentors',    i:'account-heart'},
    {k:'attendance',   t:'Attend',     i:'calendar-check'},
    {k:'reports',      t:'Reports',    i:'file-chart'},
    {k:'settings',     t:'Settings',   i:'cog'},
  ];

  const filtered    = interns.filter(i=>!searchQuery||JSON.stringify(i).toLowerCase().includes(searchQuery.toLowerCase()));
  const allApps     = [...internApps, ...portalApps];
  const filteredApps = appFilter==='All' ? allApps : allApps.filter(a=>a.status===appFilter);

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Header
  // ══════════════════════════════════════════════════════════════════════════
  const renderHeader = () => (
    <LinearGradient colors={['#B45309','#D97706']} style={st.header}>
      <View style={st.headerRow}>
        <TouchableOpacity onPress={()=>navigation?.goBack()} style={st.hBtn}><Ionicons name="arrow-back" size={22} color="#FFF"/></TouchableOpacity>
        {companyLogo?<Image source={{uri:companyLogo}} style={{width:28,height:28,borderRadius:14,marginLeft:6}}/>:<MaterialCommunityIcons name="school" size={22} color="#FFF" style={{marginLeft:6}}/>}
        <View style={{flex:1,marginLeft:8}}>
          <Text style={st.hTitle}>Internship Management</Text>
          {companyName?<Text style={{color:'#FDE68A',fontSize:11}}>{companyName}</Text>:null}
        </View>
        {stats.newPortalApps>0&&(
          <View style={{backgroundColor:'#EF4444',borderRadius:10,paddingHorizontal:8,paddingVertical:3,marginRight:6}}>
            <Text style={{color:'#FFF',fontSize:11,fontWeight:'700'}}>{stats.newPortalApps} new</Text>
          </View>
        )}
        <TouchableOpacity onPress={generatePDF} style={st.hBtn}><MaterialCommunityIcons name="file-pdf-box" size={22} color="#FFF"/></TouchableOpacity>
      </View>
    </LinearGradient>
  );

  const renderStats = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.statsBar} contentContainerStyle={{paddingHorizontal:16}}>
      {[{l:'Total',v:stats.total,c:PRIMARY},{l:'Active',v:stats.active,c:'#10B981'},{l:'Completed',v:stats.completed,c:'#3B82F6'},{l:'Applications',v:allApps.length,c:'#8B5CF6'},{l:'Programs',v:programs.length,c:'#EC4899'},{l:'Avg Rating',v:stats.avgRating+'/5',c:'#F59E0B'},{l:'New Apps',v:stats.newPortalApps,c:'#EF4444'}].map((s,i)=>(
        <View key={i} style={st.statCard}><Text style={[st.statVal,{color:s.c}]}>{s.v}</Text><Text style={st.statLbl}>{s.l}</Text></View>
      ))}
    </ScrollView>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Interns tab
  // ══════════════════════════════════════════════════════════════════════════
  const renderInterns = () => (
    <View>
      <View style={st.searchRow}><MaterialCommunityIcons name="magnify" size={20} color="#9CA3AF"/><TextInput style={st.searchInput} placeholder="Search interns…" placeholderTextColor="#9CA3AF" value={searchQuery} onChangeText={setSearchQuery}/></View>
      {filtered.length===0?(
        <View style={st.empty}><MaterialCommunityIcons name="school" size={48} color="#D1D5DB"/><Text style={st.emptyTxt}>No interns found</Text>
          <TouchableOpacity style={st.addBtn} onPress={()=>setActiveTab('create')}><Text style={st.addBtnTxt}>Add Intern</Text></TouchableOpacity></View>
      ):filtered.map((intern,i)=>(
        <TouchableOpacity key={intern.id||i} style={st.card} onPress={()=>{setSelectedIntern(intern);setShowModal(true);}}>
          <View style={{flexDirection:'row',alignItems:'center'}}>
            <View style={[st.avatar,{backgroundColor:PRIMARY}]}><Text style={st.avatarTxt}>{((intern.name||'?')[0]+((intern.name||'').split(' ')[1]||'')[0]||'').toUpperCase()}</Text></View>
            <View style={{flex:1}}>
              <Text style={st.cardName}>{intern.name||'Unknown'}</Text>
              <Text style={st.cardMeta}>{intern.university||intern.course||''} · {intern.department||''}</Text>
              <Text style={st.cardMeta}>Mentor: {intern.mentor||'TBA'} · {intern.program_type||'Internship'}</Text>
            </View>
            <View style={[st.badge,{backgroundColor:(intern.status||'').toLowerCase()==='active'?'#10B981':'#6B7280'}]}><Text style={st.badgeTxt}>{intern.status||'Active'}</Text></View>
          </View>
          <View style={{flexDirection:'row',gap:6,marginTop:8,flexWrap:'wrap'}}>
            <TouchableOpacity style={[st.chip,{backgroundColor:'#7C3AED15'}]} onPress={()=>handleEvaluate(intern)}><Text style={{fontSize:10,color:'#7C3AED'}}>Evaluate</Text></TouchableOpacity>
            <TouchableOpacity style={[st.chip,{backgroundColor:'#10B98115'}]} onPress={()=>convertToFullTime(intern)}><Text style={{fontSize:10,color:'#10B981'}}>Convert to FT</Text></TouchableOpacity>
            <TouchableOpacity style={[st.chip,{backgroundColor:'#3B82F615'}]} onPress={()=>handleMakeOffer(intern)}><Text style={{fontSize:10,color:'#3B82F6'}}>Make Offer</Text></TouchableOpacity>
            <TouchableOpacity style={[st.chip,{backgroundColor:'#EF444415'}]} onPress={()=>deleteIntern(intern)}><Text style={{fontSize:10,color:'#EF4444'}}>Remove</Text></TouchableOpacity>
          </View>
        </TouchableOpacity>
      ))}
    </View>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Programs tab
  // ══════════════════════════════════════════════════════════════════════════
  const renderPrograms = () => (
    <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={{flex:1}}>
      <ScrollView style={{padding:16}}>
        <Text style={st.secTitle}>Create & Publish Program</Text>
        <View style={{backgroundColor:'#FFF7ED',borderRadius:10,padding:10,marginBottom:14,flexDirection:'row',gap:8}}>
          <MaterialCommunityIcons name="information" size={16} color="#B45309"/>
          <Text style={{flex:1,fontSize:12,color:'#92400E',lineHeight:18}}>Programs you create are automatically published to the Student Internship Portal for candidates to discover and apply.</Text>
        </View>
        {[['Title *','title','e.g. Summer Tech Internship 2025'],['Description','description','Program details, what interns will learn…'],['Duration (weeks)','duration','12'],['Max Interns','max_interns','5'],['Stipend Range','stipend_range','e.g. £500–£800/month'],['Requirements','requirements','Skills, education, comma-separated'],['Benefits','benefits','List benefits, one per line'],['Start Date','start_date','YYYY-MM-DD']].map(([l,k,ph])=>(
          <View key={k} style={st.formField}>
            <Text style={st.formLbl}>{l}</Text>
            <TextInput style={(['description','requirements','benefits'].includes(k))?[st.formInput,{height:70,textAlignVertical:'top'}]:st.formInput}
              placeholder={ph} placeholderTextColor="#9CA3AF" value={programForm[k]}
              onChangeText={v=>setProgramForm(p=>({...p,[k]:v}))}
              multiline={['description','requirements','benefits'].includes(k)}
              keyboardType={['duration','max_interns'].includes(k)?'numeric':'default'}/>
          </View>
        ))}
        <Text style={st.formLbl}>Department</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
          {DEPARTMENTS.map(d=>(
            <TouchableOpacity key={d} style={{paddingHorizontal:14,paddingVertical:7,borderRadius:16,marginRight:8,backgroundColor:programForm.department===d?PRIMARY:'#F1F5F9'}} onPress={()=>setProgramForm(p=>({...p,department:d}))}>
              <Text style={{color:programForm.department===d?'#FFF':'#6B7280',fontWeight:'600',fontSize:12}}>{d}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <Text style={st.formLbl}>Program Type</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
          {PROGRAM_TYPES.map(t=>(
            <TouchableOpacity key={t} style={{paddingHorizontal:14,paddingVertical:7,borderRadius:16,marginRight:8,backgroundColor:programForm.type===t?PRIMARY:'#F1F5F9'}} onPress={()=>setProgramForm(p=>({...p,type:t}))}>
              <Text style={{color:programForm.type===t?'#FFF':'#6B7280',fontWeight:'600',fontSize:11}}>{t}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <TouchableOpacity style={st.submitBtn} onPress={createProgram}>
          <MaterialCommunityIcons name="publish" size={18} color="#FFF"/>
          <Text style={st.submitBtnTxt}>Publish Program to Student Portal</Text>
        </TouchableOpacity>

        <Text style={[st.secTitle,{marginTop:24}]}>Published Programs ({programs.filter(p=>p.published).length}/{programs.length})</Text>
        {programs.map((p,i)=>(
          <View key={i} style={[st.card,{borderLeftWidth:4,borderLeftColor:p.published?'#10B981':PRIMARY}]}>
            <View style={{flexDirection:'row',alignItems:'flex-start'}}>
              <View style={{flex:1}}>
                <Text style={{fontWeight:'700',color:'#1F2937'}}>{p.title}</Text>
                <Text style={{fontSize:12,color:'#6B7280',marginTop:2}}>{p.type} · {p.department} · {p.duration} weeks · Max {p.max_interns}</Text>
                {p.stipend_range?<Text style={{fontSize:11,color:'#10B981',fontWeight:'600',marginTop:1}}>{p.stipend_range}</Text>:null}
              </View>
              <View style={{gap:6,alignItems:'flex-end'}}>
                <View style={[st.badge,{backgroundColor:p.published?'#10B981':'#6B7280'}]}>
                  <Text style={st.badgeTxt}>{p.published?'Live':'Draft'}</Text>
                </View>
              </View>
            </View>
            {p.description?<Text style={{fontSize:11,color:'#9CA3AF',marginTop:4}} numberOfLines={2}>{p.description}</Text>:null}
            <View style={{flexDirection:'row',gap:8,marginTop:8}}>
              <TouchableOpacity style={[st.chip,{backgroundColor:p.published?'#FEF3C715':'#ECFDF515',flex:1,justifyContent:'center',alignItems:'center'}]} onPress={()=>toggleProgramPublish(p)}>
                <Text style={{fontSize:11,color:p.published?'#D97706':'#10B981',fontWeight:'600'}}>{p.published?'Unpublish':'Publish'}</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[st.chip,{backgroundColor:'#EF444415',flex:1,justifyContent:'center',alignItems:'center'}]} onPress={()=>deleteProgram(p.id)}>
                <Text style={{fontSize:11,color:'#EF4444',fontWeight:'600'}}>Delete</Text>
              </TouchableOpacity>
            </View>
          </View>
        ))}
      </ScrollView>
    </KeyboardAvoidingView>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Applications tab (INTEGRATION CORE)
  // ══════════════════════════════════════════════════════════════════════════
  const renderApplications = () => (
    <View style={{flex:1}}>
      {/* Status filter chips */}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:16,paddingVertical:10,gap:8}}>
        {['All',...APP_STATUSES].map(s=>{
          const count = s==='All' ? allApps.length : allApps.filter(a=>a.status===s).length;
          return (
            <TouchableOpacity key={s} style={{paddingHorizontal:12,paddingVertical:6,borderRadius:16,backgroundColor:appFilter===s?appColor(s)||PRIMARY:'#F1F5F9',flexDirection:'row',alignItems:'center',gap:4}} onPress={()=>setAppFilter(s)}>
              <Text style={{fontSize:12,fontWeight:'600',color:appFilter===s?'#FFF':'#6B7280'}}>{s}</Text>
              {count>0&&<View style={{backgroundColor:appFilter===s?'rgba(255,255,255,0.3)':'#E5E7EB',borderRadius:8,paddingHorizontal:5,paddingVertical:1}}><Text style={{fontSize:10,fontWeight:'700',color:appFilter===s?'#FFF':'#6B7280'}}>{count}</Text></View>}
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      {/* Summary row */}
      <View style={{flexDirection:'row',backgroundColor:'#FFF',paddingHorizontal:16,paddingBottom:10,gap:8}}>
        {[['Total',allApps.length,'#8B5CF6'],['New',stats.newPortalApps,'#EF4444'],['Interview',allApps.filter(a=>a.status==='Interview Scheduled').length,'#3B82F6'],['Accepted',allApps.filter(a=>a.status==='Accepted').length,'#10B981']].map(([l,v,c])=>(
          <View key={l} style={{flex:1,alignItems:'center',backgroundColor:'#F8F9FA',borderRadius:8,padding:8}}>
            <Text style={{fontSize:18,fontWeight:'900',color:c}}>{v}</Text>
            <Text style={{fontSize:10,color:'#6B7280'}}>{l}</Text>
          </View>
        ))}
      </View>

      <ScrollView style={{flex:1,padding:16}} showsVerticalScrollIndicator={false}>
        {filteredApps.length===0?(
          <View style={st.empty}><MaterialCommunityIcons name="file-account" size={48} color="#D1D5DB"/><Text style={st.emptyTxt}>No applications{appFilter!=='All'?' with status: '+appFilter:''}</Text></View>
        ):filteredApps.map((app,i)=>{
          const isPortal = !!app.studentName; // came from student portal
          const name     = app.studentName||app.candidateName||app.candidate_name||'Unknown';
          const sc       = appColor(app.status||'Submitted');
          return (
            <TouchableOpacity key={app.id||i} style={[st.card,{borderLeftWidth:4,borderLeftColor:sc}]}
              onPress={()=>{setSelectedApp(app);setReviewerNotes('');setShowAppModal(true);}}>
              <View style={{flexDirection:'row',alignItems:'flex-start'}}>
                <View style={[st.avatar,{backgroundColor:sc,width:36,height:36}]}><Text style={[st.avatarTxt,{fontSize:14}]}>{(name[0]||'?').toUpperCase()}</Text></View>
                <View style={{flex:1,marginLeft:8}}>
                  <View style={{flexDirection:'row',alignItems:'center',gap:6}}>
                    <Text style={st.cardName}>{name}</Text>
                    {isPortal&&<View style={{backgroundColor:'#667eea22',borderRadius:6,paddingHorizontal:6,paddingVertical:2}}><Text style={{fontSize:9,color:'#667eea',fontWeight:'700'}}>PORTAL</Text></View>}
                  </View>
                  <Text style={st.cardMeta}>{app.studentEmail||app.candidateEmail||app.candidate_email||''}</Text>
                  <Text style={st.cardMeta}>{app.programTitle||app.position||'Internship'} · {app.studentUniversity||app.university||''}</Text>
                </View>
                <View style={[st.badge,{backgroundColor:sc+'22',maxWidth:90}]}><Text style={[st.badgeTxt,{color:sc}]} numberOfLines={1}>{app.status||'Submitted'}</Text></View>
              </View>
              <View style={{flexDirection:'row',justifyContent:'space-between',marginTop:8,alignItems:'center'}}>
                <Text style={{fontSize:11,color:'#9CA3AF'}}>Applied {fmtDate(app.submittedAt||app.created_at)}</Text>
                {app.status==='Interview Scheduled'&&app.interviewDate&&(
                  <Text style={{fontSize:11,color:'#3B82F6',fontWeight:'600'}}>📅 {fmtDate(app.interviewDate)} {app.interviewTime}</Text>
                )}
              </View>
              {/* Quick action buttons */}
              {(app.status==='Submitted'||app.status==='Under Review')&&(
                <View style={{flexDirection:'row',gap:6,marginTop:8}}>
                  <TouchableOpacity style={[st.chip,{backgroundColor:'#10B98115',flex:1,alignItems:'center'}]} onPress={()=>{setSelectedApp(app);handleAccept(app);}}>
                    <Text style={{fontSize:10,color:'#10B981',fontWeight:'600'}}>Accept</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[st.chip,{backgroundColor:'#3B82F615',flex:1,alignItems:'center'}]} onPress={()=>{setSelectedApp(app);setShowInterview(true);}}>
                    <Text style={{fontSize:10,color:'#3B82F6',fontWeight:'600'}}>Interview</Text>
                  </TouchableOpacity>
                  <TouchableOpacity style={[st.chip,{backgroundColor:'#EF444415',flex:1,alignItems:'center'}]} onPress={()=>{setSelectedApp(app);setShowRejectModal(true);}}>
                    <Text style={{fontSize:10,color:'#EF4444',fontWeight:'600'}}>Reject</Text>
                  </TouchableOpacity>
                </View>
              )}
            </TouchableOpacity>
          );
        })}
        <View style={{height:40}}/>
      </ScrollView>
    </View>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Create Intern tab
  // ══════════════════════════════════════════════════════════════════════════
  const renderCreate = () => (
    <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={{flex:1}}>
      <ScrollView style={{padding:16}}>
        <Text style={st.secTitle}>Add New Intern</Text>
        {[['Name *','name','Full name'],['Email *','email','email@university.ac.uk'],['Phone','phone','+44…'],['University','university','University name'],['Course','course','Computer Science'],['Mentor','mentor','Assigned mentor'],['Start Date','start_date','YYYY-MM-DD'],['End Date','end_date','YYYY-MM-DD'],['Stipend','stipend','£800/month']].map(([l,k,ph])=>(
          <View key={k} style={st.formField}><Text style={st.formLbl}>{l}</Text><TextInput style={st.formInput} placeholder={ph} placeholderTextColor="#9CA3AF" value={internForm[k]} onChangeText={v=>setInternForm(p=>({...p,[k]:v}))}/></View>
        ))}
        <Text style={st.formLbl}>Department</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
          {DEPARTMENTS.map(d=>(
            <TouchableOpacity key={d} style={{paddingHorizontal:14,paddingVertical:7,borderRadius:16,marginRight:8,backgroundColor:internForm.department===d?PRIMARY:'#F1F5F9'}} onPress={()=>setInternForm(p=>({...p,department:d}))}>
              <Text style={{color:internForm.department===d?'#FFF':'#6B7280',fontWeight:'600',fontSize:12}}>{d}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <Text style={st.formLbl}>Program Type</Text>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginBottom:12}}>
          {PROGRAM_TYPES.map(t=>(
            <TouchableOpacity key={t} style={{paddingHorizontal:14,paddingVertical:7,borderRadius:16,marginRight:8,backgroundColor:internForm.program_type===t?PRIMARY:'#F1F5F9'}} onPress={()=>setInternForm(p=>({...p,program_type:t}))}>
              <Text style={{color:internForm.program_type===t?'#FFF':'#6B7280',fontWeight:'600',fontSize:11}}>{t}</Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
        <TouchableOpacity style={st.submitBtn} onPress={createIntern}>
          <MaterialCommunityIcons name="account-plus" size={18} color="#FFF"/>
          <Text style={st.submitBtnTxt}>Add Intern</Text>
        </TouchableOpacity>
      </ScrollView>
    </KeyboardAvoidingView>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Assignments
  // ══════════════════════════════════════════════════════════════════════════
  const renderAssignments = () => (
    <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={{flex:1}}>
      <ScrollView style={{padding:16}}>
        <Text style={st.secTitle}>Assign Task</Text>
        {[['Intern *','intern_name','Intern name'],['Project *','project','Project name'],['Task','task','Task description'],['Deadline','deadline','YYYY-MM-DD'],['Mentor','mentor','Supervisor']].map(([l,k,ph])=>(
          <View key={k} style={st.formField}><Text style={st.formLbl}>{l}</Text><TextInput style={k==='task'?[st.formInput,{height:60,textAlignVertical:'top'}]:st.formInput} placeholder={ph} placeholderTextColor="#9CA3AF" value={assignForm[k]} onChangeText={v=>setAssignForm(p=>({...p,[k]:v}))} multiline={k==='task'}/></View>
        ))}
        <Text style={st.formLbl}>Priority</Text>
        <View style={{flexDirection:'row',gap:8,marginBottom:12}}>
          {['low','medium','high','critical'].map(p=>(
            <TouchableOpacity key={p} style={{flex:1,padding:8,borderRadius:8,backgroundColor:assignForm.priority===p?(p==='critical'?'#EF4444':p==='high'?'#F59E0B':PRIMARY):'#F1F5F9',alignItems:'center'}} onPress={()=>setAssignForm(pr=>({...pr,priority:p}))}>
              <Text style={{color:assignForm.priority===p?'#FFF':'#6B7280',fontWeight:'600',fontSize:12,textTransform:'capitalize'}}>{p}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <TouchableOpacity style={st.submitBtn} onPress={createAssignment}><MaterialCommunityIcons name="clipboard-plus" size={18} color="#FFF"/><Text style={st.submitBtnTxt}>Assign Task</Text></TouchableOpacity>
        <Text style={[st.secTitle,{marginTop:20}]}>Tasks ({assignments.length})</Text>
        {assignments.map((a,i)=>(
          <View key={i} style={[st.card,{borderLeftWidth:4,borderLeftColor:a.priority==='critical'?'#EF4444':a.priority==='high'?'#F59E0B':'#3B82F6'}]}>
            <Text style={{fontWeight:'600',color:'#1F2937'}}>{a.intern_name} — {a.project}</Text>
            <Text style={{fontSize:12,color:'#6B7280'}}>{a.task||''} · Due: {fmtDate(a.deadline)}</Text>
          </View>
        ))}
      </ScrollView>
    </KeyboardAvoidingView>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Reviews
  // ══════════════════════════════════════════════════════════════════════════
  const renderReviews = () => (
    <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={{flex:1}}>
      <ScrollView style={{padding:16}}>
        <Text style={st.secTitle}>Intern Review</Text>
        {[['Intern *','intern_name','Intern name'],['Review Period','period','e.g. Month 1, Q1 2025']].map(([l,k,ph])=>(
          <View key={k} style={st.formField}><Text style={st.formLbl}>{l}</Text><TextInput style={st.formInput} placeholder={ph} placeholderTextColor="#9CA3AF" value={reviewForm[k]} onChangeText={v=>setReviewForm(p=>({...p,[k]:v}))}/></View>
        ))}
        {['Performance','Communication','Technical','Initiative','Teamwork'].map(c=>(
          <ScoreSlider key={c} label={c} value={reviewForm[c.toLowerCase()]||0} onChange={v=>setReviewForm(p=>({...p,[c.toLowerCase()]:v}))}/>
        ))}
        <View style={st.formField}><Text style={st.formLbl}>Feedback</Text><TextInput style={[st.formInput,{height:80,textAlignVertical:'top'}]} placeholder="Detailed feedback…" value={reviewForm.feedback} onChangeText={v=>setReviewForm(p=>({...p,feedback:v}))} multiline/></View>
        <Text style={st.formLbl}>Recommendation</Text>
        <View style={{flexDirection:'row',flexWrap:'wrap',gap:8,marginBottom:12}}>
          {['Extend','Convert to FT','Complete','Needs Improvement','Terminate'].map(r=>(
            <TouchableOpacity key={r} style={{paddingHorizontal:12,paddingVertical:7,borderRadius:16,backgroundColor:reviewForm.recommendation===r?PRIMARY:'#F1F5F9'}} onPress={()=>setReviewForm(p=>({...p,recommendation:r}))}>
              <Text style={{color:reviewForm.recommendation===r?'#FFF':'#6B7280',fontWeight:'600',fontSize:12}}>{r}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <TouchableOpacity style={st.submitBtn} onPress={submitReview}><MaterialCommunityIcons name="star" size={18} color="#FFF"/><Text style={st.submitBtnTxt}>Submit Review</Text></TouchableOpacity>
        <Text style={[st.secTitle,{marginTop:20}]}>Past Reviews ({reviews.length})</Text>
        {reviews.map((r,i)=>(
          <View key={i} style={st.card}>
            <View style={{flexDirection:'row',justifyContent:'space-between'}}>
              <Text style={{fontWeight:'600',color:'#1F2937'}}>{r.intern_name}</Text>
              <Text style={{fontWeight:'700',color:scColor(parseFloat(r.overall))}}>{r.overall}/5</Text>
            </View>
            <Text style={{fontSize:12,color:'#6B7280',marginTop:2}}>{r.period||''} · {r.recommendation||''} · {fmtDate(r.created_at)}</Text>
          </View>
        ))}
      </ScrollView>
    </KeyboardAvoidingView>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Mentoring
  // ══════════════════════════════════════════════════════════════════════════
  const renderMentoring = () => (
    <ScrollView style={{padding:16}}>
      <Text style={st.secTitle}>Mentor-Intern Pairs</Text>
      {interns.filter(i=>i.mentor).map((intern,i)=>(
        <View key={i} style={st.card}>
          <View style={{flexDirection:'row',alignItems:'center'}}>
            <View style={[st.avatar,{backgroundColor:'#3B82F6',width:32,height:32}]}><MaterialCommunityIcons name="account-heart" size={16} color="#FFF"/></View>
            <View style={{flex:1,marginLeft:8}}><Text style={{fontWeight:'600',color:'#1F2937'}}>{intern.mentor}</Text><Text style={{fontSize:11,color:'#6B7280'}}>Mentor</Text></View>
            <MaterialCommunityIcons name="arrow-right" size={16} color="#9CA3AF"/>
            <View style={{flex:1,marginLeft:8}}><Text style={{fontWeight:'600',color:'#1F2937'}}>{intern.name}</Text><Text style={{fontSize:11,color:'#6B7280'}}>Intern · {intern.department||''}</Text></View>
          </View>
        </View>
      ))}
      {interns.filter(i=>!i.mentor).length>0&&(
        <View style={{marginTop:16}}>
          <Text style={[st.secTitle,{color:'#F59E0B'}]}>Unassigned ({interns.filter(i=>!i.mentor).length})</Text>
          {interns.filter(i=>!i.mentor).map((intern,i)=>(
            <View key={i} style={[st.card,{borderLeftWidth:4,borderLeftColor:'#F59E0B'}]}>
              <Text style={{fontWeight:'600',color:'#1F2937'}}>{intern.name}</Text>
              <Text style={{fontSize:12,color:'#6B7280'}}>{intern.department||''} — No mentor assigned</Text>
            </View>
          ))}
        </View>
      )}
    </ScrollView>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Attendance
  // ══════════════════════════════════════════════════════════════════════════
  const renderAttendance = () => (
    <KeyboardAvoidingView behavior={Platform.OS==='ios'?'padding':'height'} style={{flex:1}}>
      <ScrollView style={{padding:16}}>
        <Text style={st.secTitle}>Record Attendance</Text>
        {[['Intern *','intern_name','Intern name'],['Date *','date','YYYY-MM-DD'],['Notes','notes','Optional']].map(([l,k,ph])=>(
          <View key={k} style={st.formField}><Text style={st.formLbl}>{l}</Text><TextInput style={st.formInput} placeholder={ph} placeholderTextColor="#9CA3AF" value={attForm[k]} onChangeText={v=>setAttForm(p=>({...p,[k]:v}))}/></View>
        ))}
        <Text style={st.formLbl}>Type</Text>
        <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginBottom:12}}>
          {['Present','Absent','Late','WFH','Sick','Holiday'].map(t=>(
            <TouchableOpacity key={t} style={{paddingHorizontal:12,paddingVertical:6,borderRadius:14,backgroundColor:attForm.type===t?PRIMARY:'#F1F5F9'}} onPress={()=>setAttForm(p=>({...p,type:t}))}>
              <Text style={{color:attForm.type===t?'#FFF':'#6B7280',fontWeight:'600',fontSize:11}}>{t}</Text>
            </TouchableOpacity>
          ))}
        </View>
        <TouchableOpacity style={st.submitBtn} onPress={recordAttendance}><MaterialCommunityIcons name="calendar-check" size={18} color="#FFF"/><Text style={st.submitBtnTxt}>Record</Text></TouchableOpacity>
        <Text style={[st.secTitle,{marginTop:20}]}>Records ({attendance.length})</Text>
        {attendance.slice(0,20).map((a,i)=>(
          <View key={i} style={st.card}>
            <View style={{flexDirection:'row',justifyContent:'space-between'}}>
              <Text style={{fontWeight:'600',color:'#1F2937'}}>{a.intern_name}</Text>
              <View style={[st.badge,{backgroundColor:a.type==='Present'?'#10B981':a.type==='Absent'?'#EF4444':'#F59E0B'}]}><Text style={st.badgeTxt}>{a.type}</Text></View>
            </View>
            <Text style={{fontSize:11,color:'#9CA3AF'}}>{fmtDate(a.date)}{a.notes?' · '+a.notes:''}</Text>
          </View>
        ))}
      </ScrollView>
    </KeyboardAvoidingView>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Reports
  // ══════════════════════════════════════════════════════════════════════════
  const renderReports = () => (
    <ScrollView style={{padding:16}}>
      <Text style={st.secTitle}>Internship Reports</Text>
      <View style={{flexDirection:'row',flexWrap:'wrap'}}>
        {[{l:'Interns',v:interns.length,c:PRIMARY,i:'account-group'},{l:'Programs',v:programs.length,c:'#3B82F6',i:'school'},
          {l:'Tasks',v:assignments.length,c:'#10B981',i:'clipboard-list'},{l:'Reviews',v:reviews.length,c:'#F59E0B',i:'star'},
          {l:'Attendance',v:attendance.length,c:'#8B5CF6',i:'calendar-check'},{l:'Applications',v:allApps.length,c:'#EC4899',i:'file-account'},
          {l:'Portal Apps',v:portalApps.length,c:'#667eea',i:'earth'},{l:'Accepted',v:allApps.filter(a=>a.status==='Accepted').length,c:'#10B981',i:'check-circle'},
        ].map((m,i)=>(
          <View key={i} style={[st.gridCard,{width:width/2-28}]}>
            <MaterialCommunityIcons name={m.i} size={22} color={m.c}/>
            <Text style={[st.gridVal,{color:m.c}]}>{m.v}</Text>
            <Text style={st.gridLbl}>{m.l}</Text>
          </View>
        ))}
      </View>
      <TouchableOpacity style={[st.submitBtn,{backgroundColor:PRIMARY}]} onPress={generatePDF}><MaterialCommunityIcons name="file-pdf-box" size={18} color="#FFF"/><Text style={st.submitBtnTxt}>Generate PDF Report</Text></TouchableOpacity>
      <TouchableOpacity style={[st.submitBtn,{backgroundColor:'#3B82F6'}]} onPress={()=>Share.share({message:JSON.stringify({interns:interns.length,programs:programs.length,reviews:reviews.length,assignments:assignments.length,attendance:attendance.length,applications:allApps.length},null,2)})}><MaterialCommunityIcons name="export" size={18} color="#FFF"/><Text style={st.submitBtnTxt}>Export Data</Text></TouchableOpacity>
    </ScrollView>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Settings
  // ══════════════════════════════════════════════════════════════════════════
  const renderSettings = () => (
    <ScrollView style={{padding:16}}>
      <Text style={st.secTitle}>Settings</Text>
      <View style={st.section}>
        {[
          {l:'Refresh Data',i:'refresh',c:'#10B981',fn:onRefresh},
          {l:'Employee Management',i:'account-group',c:'#0e4e96',fn:()=>navigation?.navigate('EmployeeManagementScreen')},
          {l:'Evaluation System',i:'clipboard-check',c:'#7C3AED',fn:()=>navigation?.navigate('EvaluationAssessmentScreen',{companyLogo,companyName})},
          {l:'Student Portal Preview',i:'earth',c:'#667eea',fn:()=>Alert.alert('Portal Status',`${programs.filter(p=>p.published).length} programs live\n${portalApps.length} portal applications\n${stats.newPortalApps} pending review`)},
          {l:'Clear All Data',i:'delete-sweep',c:'#EF4444',fn:()=>Alert.alert('Clear','Clear all internship data?',[{text:'Cancel'},{text:'Clear',onPress:async()=>{await AsyncStorage.multiRemove([SHARED.PROGRAMS,SHARED.PORTAL_APPS,'intern_reviews','intern_assignments','intern_attendance']);setPrograms([]);setReviews([]);setAssignments([]);setAttendance([]);setPortalApps([]);}}])},
        ].map((item,i)=>(
          <TouchableOpacity key={i} style={{flexDirection:'row',alignItems:'center',paddingVertical:14,borderBottomWidth:i<4?1:0,borderBottomColor:'#F3F4F6'}} onPress={item.fn}>
            <MaterialCommunityIcons name={item.i} size={22} color={item.c} style={{marginRight:12}}/>
            <Text style={{flex:1,fontSize:15,fontWeight:'500',color:'#1F2937'}}>{item.l}</Text>
            <MaterialCommunityIcons name="chevron-right" size={20} color="#9CA3AF"/>
          </TouchableOpacity>
        ))}
      </View>
    </ScrollView>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Intern detail modal
  // ══════════════════════════════════════════════════════════════════════════
  const renderInternModal = () => {
    if(!selectedIntern) return null;
    const s = selectedIntern;
    return (
      <Modal visible={showModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowModal(false)}>
        <View style={st.modal}>
          <View style={st.modalH}>
            <Text style={st.modalTitle}>{s.name||'Intern'}</Text>
            <TouchableOpacity style={st.modalClose} onPress={()=>setShowModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity>
          </View>
          <ScrollView style={{flex:1,padding:16}}>
            <View style={{alignItems:'center',marginBottom:16}}>
              <View style={[st.avatar,{width:56,height:56,backgroundColor:PRIMARY}]}><Text style={[st.avatarTxt,{fontSize:20}]}>{((s.name||'?')[0]||'').toUpperCase()}</Text></View>
              <Text style={{fontSize:18,fontWeight:'700',color:'#1F2937',marginTop:8}}>{s.name}</Text>
              <Text style={{color:'#6B7280'}}>{s.university||s.course||'Intern'}</Text>
            </View>
            {Object.entries(s).filter(([k,v])=>typeof v!=='object'&&v!=null&&!['id','company_id','created_at'].includes(k)).slice(0,15).map(([k,v],i)=>(
              <View key={i} style={{flexDirection:'row',paddingVertical:6,borderBottomWidth:1,borderBottomColor:'#F3F4F6'}}>
                <Text style={{flex:1,fontSize:13,color:'#6B7280'}}>{k.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase())}</Text>
                <Text style={{flex:1,fontSize:13,color:'#1F2937',textAlign:'right'}}>{String(v).substring(0,80)}</Text>
              </View>
            ))}
            <View style={{flexDirection:'row',gap:8,marginTop:16}}>
              <TouchableOpacity style={[st.submitBtn,{flex:1,backgroundColor:'#7C3AED'}]} onPress={()=>{setShowModal(false);handleEvaluate(s);}}><Text style={st.submitBtnTxt}>Evaluate</Text></TouchableOpacity>
              <TouchableOpacity style={[st.submitBtn,{flex:1,backgroundColor:'#10B981'}]} onPress={()=>{setShowModal(false);convertToFullTime(s);}}><Text style={st.submitBtnTxt}>Convert FT</Text></TouchableOpacity>
            </View>
            <TouchableOpacity style={[st.submitBtn,{backgroundColor:'#3B82F6'}]} onPress={()=>{setShowModal(false);handleMakeOffer(s);}}><MaterialCommunityIcons name="file-document" size={18} color="#FFF"/><Text style={st.submitBtnTxt}>Make Offer</Text></TouchableOpacity>
            <TouchableOpacity style={[st.submitBtn,{backgroundColor:'#EF4444'}]} onPress={()=>deleteIntern(s)}><MaterialCommunityIcons name="delete" size={18} color="#FFF"/><Text style={st.submitBtnTxt}>Remove Intern</Text></TouchableOpacity>
          </ScrollView>
        </View>
      </Modal>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Application detail modal (the integration hub)
  // ══════════════════════════════════════════════════════════════════════════
  const renderAppModal = () => {
    const app = selectedApp; if(!app) return null;
    const name = app.studentName||app.candidateName||app.candidate_name||'Applicant';
    const sc   = appColor(app.status||'Submitted');
    return (
      <Modal visible={showAppModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowAppModal(false)}>
        <View style={st.modal}>
          <View style={st.modalH}>
            <Text style={[st.modalTitle,{flex:1}]} numberOfLines={1}>{name}</Text>
            <View style={[st.badge,{backgroundColor:sc+'22',marginRight:8}]}><Text style={[st.badgeTxt,{color:sc}]}>{app.status||'Submitted'}</Text></View>
            <TouchableOpacity style={st.modalClose} onPress={()=>setShowAppModal(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity>
          </View>
          <ScrollView style={{flex:1,padding:16}} showsVerticalScrollIndicator={false}>
            {/* Program banner */}
            <LinearGradient colors={['#B45309','#D97706']} style={{borderRadius:12,padding:14,marginBottom:14}}>
              <Text style={{fontSize:14,fontWeight:'800',color:'#FFF'}}>{app.programTitle||app.position||'Internship'}</Text>
              <Text style={{fontSize:12,color:'rgba(255,255,255,0.8)',marginTop:2}}>{companyName} · Applied {fmtDate(app.submittedAt||app.created_at)}</Text>
              {app.studentUniversity&&<Text style={{fontSize:11,color:'rgba(255,255,255,0.7)',marginTop:1}}>{app.studentUniversity}</Text>}
            </LinearGradient>

            {/* Applicant info */}
            {[['Email',app.studentEmail||app.candidate_email||''],['Phone',app.studentPhone||app.phone||''],['University',app.studentUniversity||app.university||''],['Major',app.studentMajor||app.major||''],['GPA',app.studentGpa||app.gpa||''],['Availability',app.availability||''],].filter(([,v])=>v).map(([l,v])=>(
              <View key={l} style={{flexDirection:'row',paddingVertical:7,borderBottomWidth:1,borderBottomColor:'#F3F4F6'}}>
                <Text style={{width:90,fontSize:13,color:'#6B7280'}}>{l}</Text>
                <Text style={{flex:1,fontSize:13,color:'#1F2937',fontWeight:'500'}}>{v}</Text>
              </View>
            ))}

            {/* Application responses */}
            {[['Cover Letter',app.coverLetter],['Why Interested',app.whyInterested],['Relevant Experience',app.relevantExperience],['Additional Info',app.additionalInfo]].filter(([,v])=>v).map(([l,v])=>(
              <View key={l} style={{marginTop:14}}>
                <Text style={{fontSize:13,fontWeight:'700',color:'#374151',marginBottom:6}}>{l}</Text>
                <View style={{backgroundColor:'#F8F9FA',borderRadius:10,padding:12,borderLeftWidth:3,borderLeftColor:PRIMARY}}>
                  <Text style={{fontSize:13,color:'#374151',lineHeight:20}}>{v}</Text>
                </View>
              </View>
            ))}

            {/* Skills */}
            {app.studentSkills&&app.studentSkills.length>0&&(
              <View style={{marginTop:14}}>
                <Text style={{fontSize:13,fontWeight:'700',color:'#374151',marginBottom:8}}>Skills</Text>
                <View style={{flexDirection:'row',flexWrap:'wrap',gap:6}}>
                  {app.studentSkills.map((sk,i)=><View key={i} style={{backgroundColor:'#EEF2FF',borderRadius:8,paddingHorizontal:10,paddingVertical:4}}><Text style={{fontSize:12,color:'#667eea',fontWeight:'600'}}>{sk}</Text></View>)}
                </View>
              </View>
            )}

            {/* Interview details if scheduled */}
            {app.interviewDate&&(
              <View style={{marginTop:14,backgroundColor:'#EFF6FF',borderRadius:12,padding:14,borderWidth:1,borderColor:'#BFDBFE'}}>
                <Text style={{fontSize:14,fontWeight:'700',color:'#1D4ED8',marginBottom:8}}>📅 Interview Scheduled</Text>
                {[['Date',app.interviewDate],['Time',app.interviewTime],['Type',app.interviewType],['Link',app.interviewLink],['Location',app.interviewLocation],['Duration',app.interviewDuration?app.interviewDuration+' min':''],['Interviewer',app.interviewerName],['Notes',app.interviewNotes]].filter(([,v])=>v).map(([l,v])=>(
                  <View key={l} style={{flexDirection:'row',gap:8,marginBottom:4}}>
                    <Text style={{fontSize:12,color:'#3B82F6',width:80}}>{l}</Text>
                    <Text style={{flex:1,fontSize:12,color:'#1D4ED8',fontWeight:'500'}}>{v}</Text>
                  </View>
                ))}
              </View>
            )}

            {/* Reviewer notes */}
            <View style={{marginTop:14}}>
              <Text style={{fontSize:13,fontWeight:'700',color:'#374151',marginBottom:6}}>Internal Notes</Text>
              <TextInput style={[st.formInput,{height:72,textAlignVertical:'top'}]} placeholder="Add reviewer notes (not visible to applicant)…" value={reviewerNotes} onChangeText={setReviewerNotes} multiline/>
            </View>

            {/* Action buttons */}
            <Text style={[st.secTitle,{marginTop:20,marginBottom:10}]}>Actions</Text>
            <View style={{flexDirection:'row',gap:8,marginBottom:8}}>
              <TouchableOpacity style={[st.submitBtn,{flex:1,backgroundColor:'#10B981',marginTop:0}]} onPress={()=>handleAccept(app)}>
                <MaterialCommunityIcons name="check-circle" size={16} color="#FFF"/>
                <Text style={st.submitBtnTxt}>Accept</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[st.submitBtn,{flex:1,backgroundColor:'#3B82F6',marginTop:0}]} onPress={()=>{setShowInterview(true);}}>
                <MaterialCommunityIcons name="calendar-clock" size={16} color="#FFF"/>
                <Text style={st.submitBtnTxt}>Interview</Text>
              </TouchableOpacity>
            </View>
            <View style={{flexDirection:'row',gap:8,marginBottom:8}}>
              <TouchableOpacity style={[st.submitBtn,{flex:1,backgroundColor:'#F59E0B',marginTop:0}]} onPress={()=>handleShortlist(app)}>
                <MaterialCommunityIcons name="star" size={16} color="#FFF"/>
                <Text style={st.submitBtnTxt}>Shortlist</Text>
              </TouchableOpacity>
              <TouchableOpacity style={[st.submitBtn,{flex:1,backgroundColor:'#0891B2',marginTop:0}]} onPress={()=>handleUnderReview(app)}>
                <MaterialCommunityIcons name="eye" size={16} color="#FFF"/>
                <Text style={st.submitBtnTxt}>Under Review</Text>
              </TouchableOpacity>
            </View>
            <TouchableOpacity style={[st.submitBtn,{backgroundColor:'#6B7280'}]} onPress={()=>handleRequestInfo(app)}>
              <MaterialCommunityIcons name="chat-question" size={16} color="#FFF"/>
              <Text style={st.submitBtnTxt}>Request More Info</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[st.submitBtn,{backgroundColor:'#EF4444'}]} onPress={()=>setShowRejectModal(true)}>
              <MaterialCommunityIcons name="close-circle" size={16} color="#FFF"/>
              <Text style={st.submitBtnTxt}>Reject Application</Text>
            </TouchableOpacity>
            <View style={{height:32}}/>
          </ScrollView>
        </View>
      </Modal>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Interview scheduling modal
  // ══════════════════════════════════════════════════════════════════════════
  const renderInterviewModal = () => (
    <Modal visible={showInterview} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowInterview(false)}>
      <KeyboardAvoidingView style={{flex:1,backgroundColor:'#FFF'}} behavior={Platform.OS==='ios'?'padding':'height'}>
        <View style={st.modalH}>
          <Text style={st.modalTitle}>Schedule Interview</Text>
          <TouchableOpacity style={st.modalClose} onPress={()=>setShowInterview(false)}><MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity>
        </View>
        <ScrollView style={{flex:1,padding:16}} keyboardShouldPersistTaps="handled">
          {selectedApp&&(
            <View style={{backgroundColor:'#FFF7ED',borderRadius:10,padding:12,marginBottom:16,borderWidth:1,borderColor:'#FED7AA'}}>
              <Text style={{fontSize:14,fontWeight:'700',color:'#B45309'}}>{selectedApp.studentName||selectedApp.candidateName||'Applicant'}</Text>
              <Text style={{fontSize:12,color:'#D97706',marginTop:1}}>{selectedApp.programTitle||'Internship'}</Text>
            </View>
          )}
          {[['Date *','date','YYYY-MM-DD'],['Time *','time','e.g. 10:00 AM'],['Interviewer','interviewerName','Interviewer name'],['Duration (min)','duration','45'],['Video Call Link','link','https://…'],['Office Location','location','e.g. Floor 3, London'],['Notes to Candidate','notes','Any preparation or access info']].map(([l,k,ph])=>(
            <View key={k} style={st.formField}>
              <Text style={st.formLbl}>{l}</Text>
              <TextInput style={k==='notes'?[st.formInput,{height:60,textAlignVertical:'top'}]:st.formInput} placeholder={ph} placeholderTextColor="#9CA3AF" value={interviewForm[k]} onChangeText={v=>setInterviewForm(p=>({...p,[k]:v}))} multiline={k==='notes'}/>
            </View>
          ))}
          <Text style={st.formLbl}>Interview Type</Text>
          <View style={{flexDirection:'row',gap:8,marginBottom:16}}>
            {['Video Call','Phone','In-Person','Panel'].map(t=>(
              <TouchableOpacity key={t} style={{flex:1,padding:8,borderRadius:8,backgroundColor:interviewForm.type===t?'#3B82F6':'#F1F5F9',alignItems:'center'}} onPress={()=>setInterviewForm(p=>({...p,type:t}))}>
                <Text style={{color:interviewForm.type===t?'#FFF':'#6B7280',fontWeight:'600',fontSize:11,textAlign:'center'}}>{t}</Text>
              </TouchableOpacity>
            ))}
          </View>
          <TouchableOpacity style={[st.submitBtn,{backgroundColor:'#3B82F6'}]} onPress={handleScheduleInterview}>
            <MaterialCommunityIcons name="calendar-plus" size={18} color="#FFF"/>
            <Text style={st.submitBtnTxt}>Confirm & Notify Candidate</Text>
          </TouchableOpacity>
          <View style={{height:32}}/>
        </ScrollView>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER: Rejection modal
  // ══════════════════════════════════════════════════════════════════════════
  const renderRejectModal = () => (
    <Modal visible={showRejectModal} animationType="fade" transparent onRequestClose={()=>setShowRejectModal(false)}>
      <KeyboardAvoidingView style={{flex:1,backgroundColor:'rgba(0,0,0,0.5)',justifyContent:'center',padding:20}} behavior={Platform.OS==='ios'?'padding':'height'}>
        <View style={{backgroundColor:'#FFF',borderRadius:16,padding:20}}>
          <Text style={{fontSize:17,fontWeight:'800',color:'#1F2937',marginBottom:14}}>Reject Application</Text>
          {selectedApp&&<Text style={{fontSize:13,color:'#6B7280',marginBottom:14}}>Rejecting {selectedApp.studentName||'this applicant'} for {selectedApp.programTitle}</Text>}
          <Text style={st.formLbl}>Reason (will be shown to candidate)</Text>
          <TextInput style={[st.formInput,{height:80,textAlignVertical:'top',marginBottom:16}]}
            placeholder="Thank the applicant and explain why they weren't selected…"
            value={rejectReason} onChangeText={setRejectReason} multiline/>
          <View style={{flexDirection:'row',gap:10}}>
            <TouchableOpacity style={{flex:1,paddingVertical:13,borderRadius:12,backgroundColor:'#F1F5F9',alignItems:'center'}} onPress={()=>{setShowRejectModal(false);setRejectReason('');}}>
              <Text style={{fontWeight:'700',color:'#374151'}}>Cancel</Text>
            </TouchableOpacity>
            <TouchableOpacity style={{flex:2,paddingVertical:13,borderRadius:12,backgroundColor:'#EF4444',alignItems:'center'}} onPress={()=>handleReject(selectedApp)}>
              <Text style={{fontWeight:'700',color:'#FFF'}}>Confirm Rejection</Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // ══════════════════════════════════════════════════════════════════════════
  // ROUTER
  // ══════════════════════════════════════════════════════════════════════════
  const wrap = ch => (
    <ScrollView style={{flex:1}} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[PRIMARY]}/>} showsVerticalScrollIndicator={false}>
      {ch}<View style={{height:40}}/>
    </ScrollView>
  );

  const renderContent = () => {
    switch(activeTab) {
      case 'interns':      return wrap(renderInterns());
      case 'programs':     return renderPrograms();
      case 'applications': return renderApplications();
      case 'create':       return renderCreate();
      case 'assignments':  return renderAssignments();
      case 'reviews':      return renderReviews();
      case 'mentoring':    return wrap(renderMentoring());
      case 'attendance':   return renderAttendance();
      case 'reports':      return wrap(renderReports());
      case 'settings':     return wrap(renderSettings());
      default:             return wrap(renderInterns());
    }
  };

  if (isLoading) return (
    <SafeAreaView style={st.loading}>
      <StatusBar barStyle="dark-content"/>
      <ActivityIndicator size="large" color={PRIMARY}/>
      <Text style={st.loadingTxt}>Loading Internships…</Text>
    </SafeAreaView>
  );

  return (
    <SafeAreaView style={st.container}>
      <StatusBar barStyle="light-content" backgroundColor={PRIMARY}/>
      {renderHeader()}
      {renderStats()}
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={st.tabsRow} contentContainerStyle={{paddingHorizontal:8}}>
        {TABS.map(tab=>{
          const newBadge = tab.k==='applications' && stats.newPortalApps>0;
          return (
            <TouchableOpacity key={tab.k} style={[st.tab,activeTab===tab.k&&st.activeTab]} onPress={()=>setActiveTab(tab.k)}>
              <View style={{position:'relative'}}>
                <MaterialCommunityIcons name={tab.i} size={13} color={activeTab===tab.k?PRIMARY:'#6B7280'}/>
                {newBadge&&<View style={{position:'absolute',top:-4,right:-6,width:8,height:8,borderRadius:4,backgroundColor:'#EF4444'}}/>}
              </View>
              <Text style={[st.tabTxt,activeTab===tab.k&&{color:PRIMARY,fontWeight:'600'}]}>{tab.t}</Text>
            </TouchableOpacity>
          );
        })}
      </ScrollView>
      {renderContent()}
      {renderInternModal()}
      {renderAppModal()}
      {renderInterviewModal()}
      {renderRejectModal()}
    </SafeAreaView>
  );
}

const st = StyleSheet.create({
  container:  {flex:1,backgroundColor:'#F3F4F6'},
  loading:    {flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#F3F4F6'},
  loadingTxt: {fontSize:16,color:'#6B7280',marginTop:16},
  header:     {paddingTop:8,paddingBottom:12,paddingHorizontal:16},
  headerRow:  {flexDirection:'row',alignItems:'center'},
  hBtn:       {width:36,height:36,borderRadius:18,backgroundColor:'rgba(255,255,255,0.15)',justifyContent:'center',alignItems:'center'},
  hTitle:     {fontSize:17,fontWeight:'bold',color:'#FFF'},
  statsBar:   {flexGrow:0,backgroundColor:'#FFF',paddingVertical:8,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},
  statCard:   {alignItems:'center',marginRight:12,minWidth:50},
  statVal:    {fontSize:15,fontWeight:'bold'},
  statLbl:    {fontSize:9,color:'#6B7280',marginTop:1},
  tabsRow:    {backgroundColor:'#FFF',borderBottomWidth:1,borderBottomColor:'#E5E7EB',flexGrow:0,flexShrink:0,maxHeight:38},
  tab:        {flexDirection:'row',alignItems:'center',paddingHorizontal:10,paddingVertical:8,marginRight:2,borderRadius:16},
  activeTab:  {backgroundColor:'#FFFBEB'},
  tabTxt:     {fontSize:11,color:'#6B7280',marginLeft:4,fontWeight:'500'},
  section:    {backgroundColor:'#FFF',margin:16,marginBottom:0,padding:16,borderRadius:12,borderWidth:1,borderColor:'#E5E7EB'},
  secTitle:   {fontSize:17,fontWeight:'bold',color:'#1F2937',marginBottom:12},
  card:       {backgroundColor:'#FFF',marginHorizontal:16,marginBottom:8,padding:14,borderRadius:10,borderWidth:1,borderColor:'#E5E7EB'},
  cardName:   {fontSize:15,fontWeight:'600',color:'#1F2937'},
  cardMeta:   {fontSize:12,color:'#6B7280',marginTop:1},
  avatar:     {width:40,height:40,borderRadius:20,justifyContent:'center',alignItems:'center',marginRight:10},
  avatarTxt:  {color:'#FFF',fontWeight:'bold',fontSize:14},
  badge:      {borderRadius:10,paddingHorizontal:8,paddingVertical:3,alignSelf:'flex-start'},
  badgeTxt:   {fontSize:10,fontWeight:'600',color:'#FFF'},
  chip:       {paddingHorizontal:10,paddingVertical:4,borderRadius:12},
  empty:      {alignItems:'center',padding:32},
  emptyTxt:   {fontSize:16,color:'#6B7280',marginTop:12},
  addBtn:     {marginTop:12,backgroundColor:'#B45309',paddingHorizontal:20,paddingVertical:10,borderRadius:8},
  addBtnTxt:  {color:'#FFF',fontWeight:'600'},
  searchRow:  {flexDirection:'row',alignItems:'center',backgroundColor:'#F3F4F6',borderRadius:8,paddingHorizontal:10,paddingVertical:8,margin:16,marginBottom:8},
  searchInput:{flex:1,fontSize:14,color:'#1F2937',marginLeft:8},
  gridCard:   {backgroundColor:'#F9FAFB',borderRadius:10,padding:14,alignItems:'center',margin:4},
  gridVal:    {fontSize:18,fontWeight:'bold',marginTop:4},
  gridLbl:    {fontSize:12,color:'#6B7280',marginTop:4,textAlign:'center'},
  formField:  {marginBottom:14},
  formLbl:    {fontSize:13,fontWeight:'600',color:'#1F2937',marginBottom:5},
  formInput:  {backgroundColor:'#F3F4F6',borderRadius:8,padding:11,fontSize:14,color:'#1F2937',borderWidth:1,borderColor:'#E5E7EB'},
  submitBtn:  {flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:'#B45309',borderRadius:10,padding:14,marginTop:8},
  submitBtnTxt:{color:'#FFF',fontWeight:'700',fontSize:15,marginLeft:8},
  modal:      {flex:1,backgroundColor:'#FFF'},
  modalH:     {flexDirection:'row',alignItems:'center',justifyContent:'space-between',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},
  modalTitle: {fontSize:18,fontWeight:'bold',color:'#1F2937',flex:1},
  modalClose: {width:40,height:40,borderRadius:20,backgroundColor:'#F3F4F6',justifyContent:'center',alignItems:'center'},
  progressLabel:{fontSize:12,color:'#6B7280',fontWeight:'600'},
});
